import React from 'react';
import {
    SpandraadsysteemUnitBasisgegevens,
    SpandraadsysteemUnitData,
} from '../../../../../common/src/domain/customInspectionTypes/amsterdamOverspanningen/inspectieOverspanningen';
import {
    CollapsiblePanel,
    EditPanel,
    EditPanelCell,
    EditPanelCellLabel,
    EditPanelFilterSelect,
    EditPanelInput,
    EditPanelRow,
    PanelBody,
    Tray,
} from '../../../components/elements';
import { ISurveyTemplateUnitData } from '../../../components/Surveys/ISurveyTemplateUnitData';
import { BasisgegevensValue } from './components/BasisgegevensValue';
import { EditPanelStraatBagSelect } from './EditPanelStraatBagSelect';
import { EditPanelYesNoEmptyRow } from './EditPanelYesNoEmptyRow';
import { SurveyEditPhotosPanel } from './SurveyEditPhotosPanel';
import { useInspectieOverspanningenState } from './useInspectieOverspanningenState';

export function SurveyEditAoInspectieOverspanningenUnitSpandraadsysteem(
    props: { surveyId: string; objectId: string; onItemChanged: () => Promise<void> } & ISurveyTemplateUnitData,
) {
    const {
        basisgegevensState,
        formState,
        save: saveDatabase,
        setFromState,
    } = useInspectieOverspanningenState<SpandraadsysteemUnitBasisgegevens, SpandraadsysteemUnitData>(props.surveyId, props.unitId, props.objectId);

    async function save(data: SpandraadsysteemUnitData) {
        await saveDatabase(data);
        await props.onItemChanged();
    }

    async function onChange(name: keyof SpandraadsysteemUnitData, value: any, shouldSave: boolean) {
        if (!formState) {
            throw new Error('formState is not set');
        }
        const newFormState = { ...formState, [name]: value };
        // reset fields if typeGedetailleerdMutatie changes
        if (name === 'typeGedetailleerdMutatie') {
            let shouldReset = false;
            if (!value) {
                shouldReset = true;
            } else if (formState.typeGedetailleerdMutatie) {
                // All fields expect Denhalon are RVS
                if (value === 'Denhalon' && formState.typeGedetailleerdMutatie !== 'Denhalon') {
                    shouldReset = true;
                } else if (value !== 'Denhalon' && formState.typeGedetailleerdMutatie === 'Denhalon') {
                    shouldReset = true;
                }
            }
            if (shouldReset) {
                newFormState.schadeAanGaffelterminal = undefined;
                newFormState.ontbrekendeOnderdelenAanGaffelterminal = undefined;
                newFormState.onjuisteMontage = undefined;
                newFormState.schadeAanSpandraadklem = undefined;
            }
        }
        setFromState(newFormState);
        if (shouldSave) {
            await save(newFormState);
        }
    }
    async function onBlur() {
        if (!formState) {
            throw new Error('formState is not set');
        }
        await save(formState);
    }

    if (formState === undefined) {
        return null;
    }

    let material: 'rvs' | 'denhalon' | undefined = undefined;
    if (formState.typeGedetailleerdMutatie) {
        const typeGedetailleerd = formState.typeGedetailleerdMutatie;
        if (typeGedetailleerd !== undefined) {
            if (typeGedetailleerd === 'Denhalon') {
                material = 'denhalon';
            } else {
                material = 'rvs';
            }
        }
    }

    return (
        <Tray>
            <EditPanel bottomMargin={true} noPadding={true}>
                <CollapsiblePanel heading={{ dark: true, text: `${props.unitName} inventarisatie`, initialIsClosed: false }}>
                    <PanelBody isForm={true}>
                        <EditPanelRow>
                            <EditPanelCellLabel rowCount={2} text="Straat (BAG)"></EditPanelCellLabel>
                            <EditPanelCell rowCount={8}>
                                <BasisgegevensValue value={basisgegevensState.straatbag} />
                            </EditPanelCell>
                        </EditPanelRow>
                        <EditPanelRow>
                            <EditPanelCell rowCount={2}></EditPanelCell>
                            <EditPanelCell rowCount={8}>
                                <EditPanelStraatBagSelect
                                    onChange={(value) => void onChange('straatBagMutatie', value ?? undefined, true)}
                                    value={formState.straatBagMutatie}
                                />
                            </EditPanelCell>
                        </EditPanelRow>
                        <EditPanelRow>
                            <EditPanelCellLabel rowCount={2} text="Type gedetailleerd"></EditPanelCellLabel>
                            <EditPanelCell rowCount={8}>
                                <EditPanelFilterSelect
                                    name="typeGedetailleerdMutatie"
                                    options={[
                                        { text: 'RVS316 6mm zonder buitenmantel', value: 'RVS316 6mm zonder buitenmantel' },
                                        { text: 'RVS316 6mm met buitenmantel', value: 'RVS316 6mm met buitenmantel' },
                                        { text: 'RVS316 8mm zonder buitenmantel', value: 'RVS316 8mm zonder buitenmantel' },
                                        { text: 'RVS316 8mm met buitenmantel', value: 'RVS316 8mm met buitenmantel' },
                                        { text: 'Denhalon', value: 'Denhalon' },
                                        { text: 'Aramidevezel', value: 'Aramidevezel' },
                                    ]}
                                    value={formState.typeGedetailleerdMutatie ?? ''}
                                    handleChange={async (value) => onChange('typeGedetailleerdMutatie', value, true)}
                                />
                            </EditPanelCell>
                        </EditPanelRow>
                        <EditPanelRow>
                            <EditPanelCellLabel rowCount={2} text="Opmerking"></EditPanelCellLabel>
                            <EditPanelCell rowCount={8}>
                                <EditPanelInput
                                    name="opmerkingMutatie"
                                    value={formState.opmerkingMutatie}
                                    handleChange={async (value) => onChange('opmerkingMutatie', value, false)}
                                    handleBlur={onBlur}
                                />
                            </EditPanelCell>
                        </EditPanelRow>
                    </PanelBody>
                </CollapsiblePanel>

                <CollapsiblePanel heading={{ dark: true, text: `${props.unitName} inspectie` }}>
                    <PanelBody isForm={true}>
                        <EditPanelYesNoEmptyRow
                            label="Schade aan spandraad?"
                            name="schadeAanSpandraad"
                            value={formState.schadeAanSpandraad}
                            onChange={async (value) => onChange('schadeAanSpandraad', value, true)}
                        />
                        <EditPanelYesNoEmptyRow
                            label="Objecten van derden aan spandraad bevestigd?"
                            tooltip="Indien er objecten van derden aan de spandraad worden aangetroffen dient dit te worden gemeld bij de toezichthouder. De objecten mogen niet zonder tussenkomst van de omgevingsmanager worden verwijderd."
                            name="objectenVanDerdenAanSpandraadBevestigd"
                            value={formState.objectenVanDerdenAanSpandraadBevestigd}
                            onChange={async (value) => onChange('objectenVanDerdenAanSpandraadBevestigd', value, true)}
                        />
                        {material === 'rvs' && (
                            <>
                                <EditPanelYesNoEmptyRow
                                    label="Schade aan gaffelterminal?"
                                    name="schadeAanGaffelterminal"
                                    value={formState.schadeAanGaffelterminal}
                                    onChange={async (value) => onChange('schadeAanGaffelterminal', value, true)}
                                />
                                <EditPanelYesNoEmptyRow
                                    label="Ontbrekende onderdelen aan gaffelterminal?"
                                    name="ontbrekendeOnderdelenAanGaffelterminal"
                                    value={formState.ontbrekendeOnderdelenAanGaffelterminal}
                                    onChange={async (value) => onChange('ontbrekendeOnderdelenAanGaffelterminal', value, true)}
                                />
                            </>
                        )}
                        {material === 'denhalon' && (
                            <>
                                <EditPanelYesNoEmptyRow
                                    label="Onjuiste montage?"
                                    tooltip="De klemverbinding dient te worden afgekeurd indien de overlengte van het spandraad korter is dan 20cm."
                                    name="onjuisteMontage"
                                    value={formState.onjuisteMontage}
                                    onChange={async (value) => onChange('onjuisteMontage', value, true)}
                                />
                                <EditPanelYesNoEmptyRow
                                    label="Schade aan spandraadklem?"
                                    name="schadeAanSpandraadklem"
                                    value={formState.schadeAanSpandraadklem}
                                    onChange={async (value) => onChange('schadeAanSpandraadklem', value, true)}
                                />
                            </>
                        )}
                        <EditPanelYesNoEmptyRow
                            label="Schade aan knoop?"
                            tooltip="Keur de knoopverbinding af indien er schade of vervorming wordt geconstateerd"
                            name="schadeAanKnoop"
                            value={formState.schadeAanKnoop}
                            onChange={async (value) => onChange('schadeAanKnoop', value, true)}
                        />
                        <EditPanelRow>
                            <EditPanelCellLabel rowCount={4} text="Opmerking"></EditPanelCellLabel>
                            <EditPanelCell rowCount={8}>
                                <EditPanelInput
                                    handleBlur={onBlur}
                                    name="opmerking"
                                    value={formState.opmerking}
                                    handleChange={async (value) => onChange('opmerking', value, false)}
                                />
                            </EditPanelCell>
                        </EditPanelRow>
                    </PanelBody>
                </CollapsiblePanel>

                <SurveyEditPhotosPanel
                    objectId={props.objectId}
                    surveyId={props.surveyId}
                    unitName={props.unitName}
                    photos={formState.fotoBestanden}
                    onPhotosChanged={async (photos) => onChange('fotoBestanden', photos, true)}
                />
            </EditPanel>
        </Tray>
    );
}
